import React, { useState } from 'react';
import { ArrowLeft, Plus, BookOpen, Filter, AlertTriangle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useStrategies,
  useStrategy,
  useCreateStrategy,
  useDeleteStrategy,
  useActivateStrategy,
  useToggleUserActivation,
} from '../hooks/useExpertStrategies';
import StrategyLibraryCard from '../components/expert/StrategyLibraryCard';
import StrategyDetailModal from '../components/expert/StrategyDetailModal';
import { useAuth } from '../contexts/AuthContext';

const ExpertStrategiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createContent, setCreateContent] = useState('');
  const [createUrl, setCreateUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  // AC:8 — activation disclaimer modal state
  const [activationDisclaimerPending, setActivationDisclaimerPending] = useState<{
    id: number;
    isActive: boolean;
    isCommunity?: boolean;
  } | null>(null);
  const [activationDisclaimerChecked, setActivationDisclaimerChecked] = useState(false);

  const { data: strategies = [], isLoading, isError, error } = useStrategies({
    type: typeFilter,
    is_active: activeFilter,
  });
  const { data: selectedStrategy } = useStrategy(selectedId, {
    refetchInterval: extracting ? 2000 : false,
  });
  const createMutation = useCreateStrategy();
  const deleteMutation = useDeleteStrategy();
  const activateMutation = useActivateStrategy();
  const userActivationMutation = useToggleUserActivation();

  const handleCreate = () => {
    if (!createName.trim() || !createContent.trim()) return;
    createMutation.mutate(
      {
        name: createName.trim(),
        content: createContent.trim(),
        source_url: createUrl.trim() || undefined,
      },
      {
        onSuccess: () => {
          setShowCreate(false);
          setCreateName('');
          setCreateContent('');
          setCreateUrl('');
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this strategy? This cannot be undone.')) {
      deleteMutation.mutate(id);
      if (selectedId === id) setSelectedId(null);
    }
  };

  const handleToggleActive = (id: number, isActive: boolean) => {
    if (isActive) {
      // AC:8 — show disclaimer modal before activation
      setActivationDisclaimerPending({ id, isActive });
      setActivationDisclaimerChecked(false);
    } else {
      // Deactivation doesn't require disclaimer
      activateMutation.mutate({ id, isActive });
    }
  };

  const handleActivationConfirm = () => {
    if (!activationDisclaimerPending || !activationDisclaimerChecked) return;
    if (activationDisclaimerPending.isCommunity) {
      userActivationMutation.mutate(
        { id: activationDisclaimerPending.id, isActive: activationDisclaimerPending.isActive },
        { onSettled: () => setActivationDisclaimerPending(null) }
      );
    } else {
      activateMutation.mutate(
        { id: activationDisclaimerPending.id, isActive: activationDisclaimerPending.isActive },
        { onSettled: () => setActivationDisclaimerPending(null) }
      );
    }
  };

  const handleToggleUserActivation = (id: number, isActive: boolean) => {
    if (isActive) {
      // AC:8 — community strategies also require disclaimer checkbox on activation
      setActivationDisclaimerPending({ id, isActive, isCommunity: true });
      setActivationDisclaimerChecked(false);
    } else {
      userActivationMutation.mutate({ id, isActive });
    }
  };

  const activeCount = strategies.filter((s) => {
    if (s.type === 'community') return s.user_is_active ?? true;
    return s.is_active;
  }).length;
  const communityCount = strategies.filter((s) => s.type === 'community').length;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-5xl mx-auto px-4 py-6 pb-20 lg:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/trading')}
              className="text-slate-400 hover:text-white p-2 transition min-h-[44px] flex items-center"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BookOpen size={20} className="text-purple-400" />
                Strategy Library
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {strategies.length} strategies, {activeCount} active, {communityCount} community
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 rounded-lg transition"
          >
            <Plus size={14} />
            New Strategy
          </button>
        </div>

        {/* AC:7 — Strategy Library informational banner (non-dismissible) */}
        <div className="flex items-start gap-3 p-3 mb-5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            Strategies on this platform are informational tools only and do not constitute
            financial advice. Activation of a strategy does not guarantee profitable outcomes.
            my investments is not a licensed FSP.
          </span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Filter size={12} />
            Type:
          </div>
          {[
            { label: 'All', value: undefined },
            { label: 'Private', value: 'user_private' },
            { label: 'Community', value: 'community' },
          ].map((opt) => (
            <button
              key={opt.label}
              onClick={() => setTypeFilter(opt.value)}
              className={`text-xs px-2.5 py-1 rounded-lg border transition ${
                typeFilter === opt.value
                  ? 'border-purple-500/50 bg-purple-500/10 text-purple-300'
                  : 'border-slate-700 text-slate-400 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <div className="w-px h-5 bg-slate-700 mx-1" />
          <div className="flex items-center gap-1.5 text-xs text-slate-400">Status:</div>
          {[
            { label: 'All', value: undefined as boolean | undefined },
            { label: 'Active', value: true },
            { label: 'Inactive', value: false },
          ].map((opt) => (
            <button
              key={opt.label}
              onClick={() => setActiveFilter(opt.value)}
              className={`text-xs px-2.5 py-1 rounded-lg border transition ${
                activeFilter === opt.value
                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
                  : 'border-slate-700 text-slate-400 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            <AlertTriangle size={16} />
            <span>Failed to load strategies: {error instanceof Error ? error.message : 'Unknown error'}</span>
          </div>
        )}

        {/* Strategy grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
          </div>
        ) : strategies.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen size={40} className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No strategies yet</p>
            <p className="text-xs text-slate-500 mt-1">
              Create one manually or extract from a video in Expert Chat
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {strategies.map((s) => (
              <StrategyLibraryCard
                key={s.id}
                strategy={s}
                isOwner={user?.id === s.user_id}
                onView={setSelectedId}
                onToggleActive={handleToggleActive}
                onToggleUserActivation={handleToggleUserActivation}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Detail modal */}
        {selectedStrategy && selectedId !== null && (
          <StrategyDetailModal
            strategy={selectedStrategy}
            currentUserId={user?.id}
            isAdmin={user?.is_admin === true}
            onClose={() => { setSelectedId(null); setExtracting(false); }}
            onExtractingChange={setExtracting}
          />
        )}

        {/* Create modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-xl shadow-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">New Strategy</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Name</label>
                  <input
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="e.g., ICT Silver Bullet"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Strategy Content</label>
                  <textarea
                    className="w-full h-48 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white resize-y"
                    placeholder="Describe entry rules, exit rules, risk management..."
                    value={createContent}
                    onChange={(e) => setCreateContent(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Source URL (optional)</label>
                  <input
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="https://youtube.com/..."
                    value={createUrl}
                    onChange={(e) => setCreateUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setShowCreate(false)}
                  className="text-xs px-4 py-2 rounded-lg border border-slate-600 text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!createName.trim() || !createContent.trim() || createMutation.isPending}
                  className="text-xs px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AC:8 — Activation disclaimer modal */}
        {activationDisclaimerPending && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">Activate Strategy</h2>
              <p className="text-sm text-slate-300 mb-4">
                You are about to activate a strategy from the library.
              </p>
              <label className="flex items-start gap-3 cursor-pointer mb-5 select-none">
                <input
                  type="checkbox"
                  checked={activationDisclaimerChecked}
                  onChange={(e) => setActivationDisclaimerChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500/30"
                />
                <span className="text-sm text-slate-300">
                  This strategy is for informational use only and does not constitute financial
                  advice.
                </span>
              </label>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setActivationDisclaimerPending(null)}
                  className="text-xs px-4 py-2 rounded-lg border border-slate-600 text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleActivationConfirm}
                  disabled={
                    !activationDisclaimerChecked ||
                    activateMutation.isPending ||
                    userActivationMutation.isPending
                  }
                  className="text-xs px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(activateMutation.isPending || userActivationMutation.isPending) ? 'Activating...' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpertStrategiesPage;
